import React, { useEffect, useState } from 'react';
import { onAuthStateChanged, signInWithPopup, signOut, User } from 'firebase/auth';
import { addDoc, collection, doc, getDoc, onSnapshot, orderBy, query, serverTimestamp } from 'firebase/firestore';
import { auth, db, googleProvider } from '../../firebase';
import AdminPanel from './AdminPanel';
import styles from './LoanTracker.module.css';

type Status = 'loading' | 'signedOut' | 'checkingAccess' | 'unauthorized' | 'authorized';

type Compounding = 'yearly' | 'monthly';

type LoanId = 'earnest' | 'nagu';

const LOANS: { id: LoanId; label: string }[] = [
  { id: 'earnest', label: 'Earnest' },
  { id: 'nagu', label: 'Nagu' },
];

type LedgerEntry = {
  date: string;
  amount: string;
  type: 'disbursement' | 'repayment';
  // Only set for disbursements — what the outstanding balance accrues at.
  rate?: number;
  compounding?: Compounding;
};

type LoanRole = 'lender' | 'borrower' | 'reader';
type Role = 'superuser' | LoanRole;

// Set to true to bypass Google sign-in during local dev (see the "Viewing
// as" role switcher below); must stay false for real per-user authorization.
const AUTH_DISABLED_FOR_DEV = false;

// TEMPORARY: no sign-in required, everyone sees every loan as a reader
// (no Disburse/Repay, no Admin). Also requires firestore.rules to allow
// public reads on /loans/**/entries — see the matching TEMPORARY comment
// there. Flip both back to restore the real login gate.
const PUBLIC_READ_ONLY_MODE = true;

function getTodayLocal(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Renders a "yyyy-mm-dd" string as "Jul 26, 2026".
function formatDateReadable(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Strips input down to a plain "1234.56"-shaped string, capping to 2 decimals.
function sanitizeAmountInput(raw: string): string {
  let value = raw.replace(/[^0-9.]/g, '');
  const parts = value.split('.');
  if (parts.length > 2) {
    value = parts[0] + '.' + parts.slice(1).join('');
  }
  const dotIndex = value.indexOf('.');
  if (dotIndex !== -1 && value.length - dotIndex - 1 > 2) {
    value = value.slice(0, dotIndex + 3);
  }
  return value;
}

// Renders a plain "1234.56" string as "$1,234.56" while still being typed.
function formatAmountInput(raw: string): string {
  if (!raw) return '';
  const [intPart, decPart] = raw.split('.');
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decPart !== undefined ? `$${withCommas}.${decPart}` : `$${withCommas}`;
}

// Renders a stored amount (or a running balance) as a finished "$1,234.56",
// or "-$1,234.56" for negative balances.
function formatAmountDisplay(raw: string | number): string {
  const num = typeof raw === 'string' ? Number(raw) : raw;
  if (Number.isNaN(num)) return '$0.00';
  const sign = num < 0 ? '-' : '';
  return `${sign}$${Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function yearsBetween(fromISO: string, toISO: string): number {
  const msPerYear = 365.25 * 24 * 60 * 60 * 1000;
  return (new Date(toISO).getTime() - new Date(fromISO).getTime()) / msPerYear;
}

// Standard compound interest: A = P * (1 + r/n)^(n*t).
function compoundedValue(principal: number, annualRate: number, compounding: Compounding, years: number): number {
  if (years <= 0) return principal;
  const periodsPerYear = compounding === 'monthly' ? 12 : 1;
  return principal * (1 + annualRate / periodsPerYear) ** (periodsPerYear * years);
}

// Repayments should never exceed disbursements, so the balance is always
// shown as an outstanding (disbursed) amount, never as a repayment. Each
// disbursement accrues compound interest from its own date up to `asOfISO`.
// Repayments are a fixed historical sum — projecting into the future
// assumes no further repayments are made before that date.
function computeBalanceAt(entries: LedgerEntry[], asOfISO: string): number {
  const totalDisbursed = entries
    .filter((entry) => entry.type === 'disbursement')
    .reduce(
      (sum, entry) =>
        sum +
        compoundedValue(Number(entry.amount), entry.rate ?? 0, entry.compounding ?? 'yearly', yearsBetween(entry.date, asOfISO)),
      0
    );
  const totalRepaid = entries
    .filter((entry) => entry.type === 'repayment')
    .reduce((sum, entry) => sum + Number(entry.amount), 0);
  return totalDisbursed - totalRepaid;
}

// Access and role are both decided by firestore.rules (via the /roles
// collection), not by anything in this component. Signing in only proves
// who you are; a Firestore read/write against /roles or /loans/**/entries
// is what actually gets allowed or denied, server-side.
const LoanTracker: React.FC = () => {
  const [status, setStatus] = useState<Status>(
    PUBLIC_READ_ONLY_MODE || AUTH_DISABLED_FOR_DEV ? 'authorized' : 'loading'
  );
  const [user, setUser] = useState<User | null>(null);
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(getTodayLocal);
  const [rate, setRate] = useState('5');
  const [compounding, setCompounding] = useState<Compounding>('yearly');
  const [repayAmount, setRepayAmount] = useState('');
  const [repayDate, setRepayDate] = useState(getTodayLocal);
  const [activeLoan, setActiveLoan] = useState<LoanId | null>(null);
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [entriesLoading, setEntriesLoading] = useState(true);
  const [firestoreSuperuser, setFirestoreSuperuser] = useState(false);
  const [firestoreLoanRoles, setFirestoreLoanRoles] = useState<Partial<Record<LoanId, LoanRole>>>({});
  const [requestSent, setRequestSent] = useState(false);
  const [view, setView] = useState<'loans' | 'admin'>('loans');

  // While auth is disabled there's no real signed-in user to look a role up
  // for, so a dev-only switcher stands in for "who am I testing as right now".
  const [devRole, setDevRole] = useState<Role>('lender');

  // Role is per-loan — someone can be a lender on one loan and a reader on
  // another — so this reflects whichever loan tab is currently active.
  // Superusers are exempt from that entirely and always act as superuser.
  const role: Role = PUBLIC_READ_ONLY_MODE
    ? 'reader'
    : AUTH_DISABLED_FOR_DEV
    ? devRole
    : firestoreSuperuser
    ? 'superuser'
    : (activeLoan && firestoreLoanRoles[activeLoan]) || 'reader';

  // Superusers see every loan regardless of their `loanRoles` map; everyone
  // else is scoped to whichever loan(s) they were granted a role on.
  const accessibleLoans: LoanId[] =
    PUBLIC_READ_ONLY_MODE || AUTH_DISABLED_FOR_DEV || firestoreSuperuser
      ? LOANS.map((loan) => loan.id)
      : (Object.keys(firestoreLoanRoles) as LoanId[]);

  useEffect(() => {
    if (PUBLIC_READ_ONLY_MODE || AUTH_DISABLED_FOR_DEV) {
      return;
    }

    return onAuthStateChanged(auth, async (nextUser) => {
      setUser(nextUser);

      if (!nextUser) {
        setStatus('signedOut');
        return;
      }

      setStatus('checkingAccess');
      try {
        const roleSnap = await getDoc(doc(db, 'roles', nextUser.email ?? ''));
        if (!roleSnap.exists()) {
          setStatus('unauthorized');
          return;
        }
        const data = roleSnap.data();
        setFirestoreSuperuser(data.superuser === true);
        setFirestoreLoanRoles((data.loanRoles ?? {}) as Partial<Record<LoanId, LoanRole>>);
        setStatus('authorized');
      } catch {
        setStatus('unauthorized');
      }
    });
  }, []);

  // Once we know which loan(s) are accessible, default to the first one.
  useEffect(() => {
    if (activeLoan === null && accessibleLoans.length > 0) {
      setActiveLoan(accessibleLoans[0]);
    }
  }, [activeLoan, accessibleLoans]);

  // Live-updates the ledger for whichever loan tab is active, once authorized.
  useEffect(() => {
    if (status !== 'authorized' || activeLoan === null) {
      return;
    }

    setEntriesLoading(true);
    const entriesQuery = query(collection(db, 'loans', activeLoan, 'entries'), orderBy('date'));
    return onSnapshot(
      entriesQuery,
      (snapshot) => {
        setEntries(snapshot.docs.map((d) => d.data() as LedgerEntry));
        setEntriesLoading(false);
      },
      (err) => {
        // Expected when AUTH_DISABLED_FOR_DEV is true: there's no real
        // Firebase Auth session, so the rules deny the read.
        console.error('Failed to load entries', err);
        setEntriesLoading(false);
      }
    );
  }, [status, activeLoan]);

  const handleSignIn = () => {
    signInWithPopup(auth, googleProvider).catch(() => {
      // User closed the popup or sign-in failed; state stays 'signedOut'.
    });
  };

  const handleSignOut = () => {
    signOut(auth);
  };

  const handleRequestAccess = () => {
    if (!user?.email) return;
    addDoc(collection(db, 'accessRequests'), {
      name: user.displayName ?? '',
      email: user.email,
      requestedAt: serverTimestamp(),
    })
      .then(() => setRequestSent(true))
      .catch((err) => console.error('Failed to send access request', err));
  };

  const addEntry = (entry: LedgerEntry) => {
    if (activeLoan === null) return;
    addDoc(collection(db, 'loans', activeLoan, 'entries'), entry).catch((err) => {
      console.error('Failed to save entry', err);
    });
  };

  const handleDisburse = (e: React.FormEvent) => {
    e.preventDefault();
    addEntry({ date, amount, type: 'disbursement', rate: Number(rate) / 100, compounding });
    setAmount('');
    setDate(getTodayLocal());
    setRate('5');
    setCompounding('yearly');
  };

  const handleRepay = (e: React.FormEvent) => {
    e.preventDefault();
    addEntry({ date: repayDate, amount: repayAmount, type: 'repayment' });
    setRepayAmount('');
    setRepayDate(getTodayLocal());
  };

  const today = getTodayLocal();
  const PROJECTION_DATE = '2027-07-01';
  const balance = computeBalanceAt(entries, today);
  const projectedBalance = computeBalanceAt(entries, PROJECTION_DATE);

  return (
    <div className={styles.page}>
      <a href="/" className={styles.backButton} aria-label="Back to blog">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5" />
          <path d="M12 19l-7-7 7-7" />
        </svg>
        Blog
      </a>

      {user && !AUTH_DISABLED_FOR_DEV && (
        <button className={styles.signOutButton} onClick={handleSignOut}>
          Sign out
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <path d="M16 17l5-5-5-5" />
            <path d="M21 12H9" />
          </svg>
        </button>
      )}

      {status !== 'authorized' && (
        <div className={styles.content}>
          {status === 'loading' && <p className={styles.subtitle}>Loading&hellip;</p>}

          {status === 'signedOut' && (
            <>
              <h1 className={styles.title}>Loan Tracker</h1>
              <button className={`${styles.button} ${styles.lightButton}`} onClick={handleSignIn}>
                <svg width="18" height="18" viewBox="0 0 18 18">
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.617z" />
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" />
                  <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z" />
                  <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" />
                </svg>
                Sign in with Google
              </button>
            </>
          )}

          {status === 'checkingAccess' && <p className={styles.subtitle}>Checking access&hellip;</p>}

          {status === 'unauthorized' && (
            <>
              <h1 className={styles.title}>Access denied</h1>
              <p className={styles.subtitle}>{user?.email} is not authorized to view this page.</p>
              {requestSent ? (
                <p className={styles.subtitle}>Request sent.</p>
              ) : (
                <button className={styles.subtleLink} onClick={handleRequestAccess}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16v16H4z" />
                    <path d="M4 6l8 6 8-6" />
                  </svg>
                  Request Access
                </button>
              )}
            </>
          )}
        </div>
      )}

      {status === 'authorized' && (
        <div className={styles.dashboard}>
          <div className={styles.tabBar}>
            {LOANS.filter((loan) => accessibleLoans.includes(loan.id)).map((loan) => (
              <button
                key={loan.id}
                className={`${styles.tab} ${view === 'loans' && activeLoan === loan.id ? styles.tabActive : ''}`}
                onClick={() => {
                  setView('loans');
                  setActiveLoan(loan.id);
                }}
              >
                {loan.label}
              </button>
            ))}
            {role === 'superuser' && (
              <button
                className={`${styles.tab} ${view === 'admin' ? styles.tabActive : ''}`}
                onClick={() => setView('admin')}
              >
                Admin
              </button>
            )}
          </div>

          {AUTH_DISABLED_FOR_DEV && (
            <div className={styles.devRoleSwitcher}>
              Viewing as:
              <select value={devRole} onChange={(e) => setDevRole(e.target.value as Role)}>
                <option value="superuser">Superuser</option>
                <option value="lender">Lender</option>
                <option value="borrower">Borrower</option>
                <option value="reader">Reader</option>
              </select>
            </div>
          )}
          {view === 'admin' && <AdminPanel />}

          {view === 'loans' && (
          <div className={styles.layout}>
            {role !== 'reader' && (
            <div className={styles.formColumn}>
              {(role === 'lender' || role === 'superuser') && (
                <div className={styles.formPanel}>
                  <form className={styles.form} onSubmit={handleDisburse}>
                    <div className={styles.fieldRow}>
                      <label className={styles.field}>
                        Amount
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="$0.00"
                          value={formatAmountInput(amount)}
                          onChange={(e) => setAmount(sanitizeAmountInput(e.target.value))}
                          required
                        />
                      </label>
                      <label className={styles.field}>
                        Date
                        <input
                          type="date"
                          value={date}
                          onChange={(e) => setDate(e.target.value)}
                          required
                        />
                      </label>
                    </div>
                    <div className={styles.fieldRow}>
                      <label className={styles.field}>
                        Interest Rate
                        <div className={styles.inputSuffix}>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={rate}
                            onChange={(e) => setRate(e.target.value)}
                            required
                          />
                          <span>%</span>
                        </div>
                      </label>
                      <label className={styles.field}>
                        Compounding
                        <select
                          value={compounding}
                          onChange={(e) => setCompounding(e.target.value as Compounding)}
                        >
                          <option value="yearly">Yearly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </label>
                    </div>
                    <button
                      type="submit"
                      className={`${styles.button} ${styles.accentButton}`}
                      disabled={!amount || Number(amount) === 0}
                    >
                      Disburse
                    </button>
                  </form>
                </div>
              )}

              {(role === 'borrower' || role === 'superuser') && (
                <div className={styles.formPanel}>
                  <form className={styles.form} onSubmit={handleRepay}>
                    <div className={styles.fieldRow}>
                      <label className={styles.field}>
                        Amount
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="$0.00"
                          value={formatAmountInput(repayAmount)}
                          onChange={(e) => setRepayAmount(sanitizeAmountInput(e.target.value))}
                          required
                        />
                      </label>
                      <label className={styles.field}>
                        Date
                        <input
                          type="date"
                          value={repayDate}
                          onChange={(e) => setRepayDate(e.target.value)}
                          required
                        />
                      </label>
                    </div>
                    <button
                      type="submit"
                      className={`${styles.button} ${styles.accentButton}`}
                      disabled={!repayAmount || Number(repayAmount) === 0}
                    >
                      Repay
                    </button>
                  </form>
                </div>
              )}

            </div>
            )}

            <div className={styles.ledgerPanel}>
              {role === 'reader' && <span className={styles.readOnlyBadge}>Read only</span>}
              <h2 className={styles.panelTitle}>Ledger</h2>
              <div className={styles.ledgerScroll}>
                <table className={styles.ledgerTable}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Disbursements</th>
                      <th>Repayments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entriesLoading ? (
                      <tr>
                        <td colSpan={3} className={styles.ledgerEmpty}>
                          <span className={styles.spinner} aria-label="Loading" />
                        </td>
                      </tr>
                    ) : entries.length === 0 ? (
                      <tr>
                        <td colSpan={3} className={styles.ledgerEmpty}>
                          No entries yet.
                        </td>
                      </tr>
                    ) : (
                      <>
                        {entries.map((entry, i) => (
                          <tr key={i}>
                            <td>{entry.date}</td>
                            <td>{entry.type === 'disbursement' ? formatAmountDisplay(entry.amount) : '—'}</td>
                            <td>{entry.type === 'repayment' ? formatAmountDisplay(entry.amount) : '—'}</td>
                          </tr>
                        ))}
                        <tr className={styles.balanceRow}>
                          <td>Balance ({formatDateReadable(today)})</td>
                          <td>{formatAmountDisplay(balance)}</td>
                          <td>—</td>
                        </tr>
                        <tr className={styles.balanceRow}>
                          <td>Balance ({formatDateReadable(PROJECTION_DATE)})</td>
                          <td>{formatAmountDisplay(projectedBalance)}</td>
                          <td>—</td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LoanTracker;
